import AppTitle from '../support/AppTitle.js';
import useAuth from "../auth/useAuth.js";
import Hijack from '../support/Hijack.js';
import useSettings from "../settings/useSettings.js";
import GlossaryService from '../glossary/GlossaryService.js';

import React, { useState, useEffect, useReducer, useRef, lazy, Suspense } from 'react';
import EditIcon from '@material-ui/icons/Edit';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Box from '@material-ui/core/Box';
import Tooltip from '@material-ui/core/Tooltip';
import AddIcon from '@material-ui/icons/Add';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Check from '@material-ui/icons/Check';
import Alert from '@material-ui/lab/Alert';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import { FormHelperText } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import { Helmet } from "react-helmet";
import CircularProgress from '@material-ui/core/CircularProgress';

const RichTextEditor = lazy(() => import('../support/RichTextEditor.js'));

const Glossary = () => {
    return (
        <GlossaryList />
    );
};

const EditForm = (props) => {
    const [errors, setErrors] = useState({});
    const [error, setError] = useState(null);
    const [entry, setEntry] = useReducer((state, newState) => ({ ...state, ...newState }), props.entry);
    const updateField = (field, value) => { setEntry({ [field]: value }); }
    const handleInput = (e) => { updateField(e.target.name, e.target.value); };
    const [isEditing, setIsEditing] = useState((entry.id == 0));
    const [open, setOpen] = useState(false);
    const myRef = useRef(null);
    const contentRef = useRef(null);
    const settings = useSettings();
    let location = useLocation();
    useEffect(() => {
        if (location.hash.toLowerCase() == encodeURI(("#" + entry.name).toLowerCase())) {
            myRef.current.scrollIntoView(true);
        }
       
    }, [location]);


    const showEditForm = () => {
        setIsEditing(!isEditing);
    }
    const handleCancel = () => {
        props.resetData();
        setIsEditing(false);

    }
    const handleSave = (e) => {
        if (e) e.preventDefault();        
        return GlossaryService.updateGlossary(entry).then((response) => {  
            settings.set({ "name": "Glossary last updated", "settingValue": (new Date()).getTime() });
            props.resetData();
            setIsEditing(false);
        }).catch((error) => {
            if ('response' in error && 'data' in error.response) {
                setError(error.response.data.title);
                setErrors(error.response.data.errors);
            }
        });
    };
    const handleDelete = (entry) => {
        GlossaryService.deleteGlossary(entry).then((response) => {
            props.onDelete(entry);
            settings.set({ "name": "Glossary last updated", "settingValue": (new Date()).getTime() });
            setOpen(false);
        });
    };
    const showConfirmDialog = () => {
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
    };

    return (
        <div>
            {isEditing ?
                <div>   
                    <h3>{entry.id == 0 ? <span>Create new entry</span> : <span>Editing {entry.name}</span>}</h3>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start"><TextField name="name" variant="outlined" label="Name" defaultValue={entry.name} error={'name' in errors || 'Name' in errors } onChange={handleInput} helperText="Text 1-200 characters" className="h3 removeMargin" />
                        <Tooltip title={"Cancel editing " + entry.name}><IconButton disableRipple={true} size="small" color="primary" onClick={handleCancel}><CloseIcon /></IconButton></Tooltip>            
                </Box>
                    <div><TextField name="description" label="Description" error={'description' in errors} multiline value={entry.description} fullWidth onChange={handleInput} helperText="0-1000 characters" /></div>
                    <FormHelperText>Content</FormHelperText>
                    <Suspense fallback={<CircularProgress />}>
                        <RichTextEditor
                            value={entry?.content}
                            onEditorChange={(content) => { updateField('content', content); }}
                        />
                    </Suspense>
                   
                    {error ? <Alert severity="error">{error}</Alert> : ""}
                    <Button color="primary" onClick={handleSave} variant="contained" startIcon={<Check />}>Save</Button>
                </div>

                :
                <div>
           
                        <h3 ref={myRef}><Box display="flex" justifyContent="space-between"><span>{entry.name}</span> 
                        {props.canContribute ?
                            <span>
                                <Tooltip title={"Edit " + entry.name}><IconButton disableRipple={true} size="small" color="primary" onClick={showEditForm}><EditIcon /></IconButton></Tooltip>
                                <Tooltip title={"Delete " + entry.name}><IconButton disableRipple={true} size="small" color="primary" onClick={showConfirmDialog}><DeleteForeverIcon /></IconButton></Tooltip>
                            </span>
                            :
                            <span />
                        }
                    </Box></h3>
                    <p>{entry.description}</p>
                    <Hijack><p ref={contentRef} dangerouslySetInnerHTML={{ __html: entry.content }} />
                    </Hijack>
                    <Dialog
                        open={open}
                        onClose={handleClose}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title" disableTypography> <Typography variant="h2">{"Are you sure you wish to delete " + entry.name + "?"}</Typography></DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                WARNING: Deleting a glossary entry will not automatically remove links from content pages!
          </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => handleDelete(entry)} color="primary" variant="contained">
                                Yes
          </Button>
                            <Button onClick={handleClose} color="primary" variant="contained" autoFocus>
                                No
          </Button>
                        </DialogActions>
                    </Dialog>
                </div>
            }
          
        </div>
        );
}
const GlossaryList = () => {
    const auth = useAuth();
    const [glossaryEntries, setGlossaryEntries] = useState([]);
    const [filter, setFilter] = useState("");
    const [tempFilter, setTempFilter] = useState("");
    const [sort, setSort] = useState("name");
    const [dir, setDir] = useState("asc");
    const [page, setPage] = useState(0);
    const pageSizes = [0];
    const settings = useSettings();

 
    const [pageSize, setPageSize] = useState(pageSizes[0]);
  
    const [total, setTotal] = useState(0);
    const [canContribute, setCanContribute] = useState(false);


    const [lastGlossaryTime, setLastGlossaryTime] = useState(0);
    const updateData = () => {
        var updated = settings.get("Glossary last updated");
        if (lastGlossaryTime != updated) {
            setLastGlossaryTime(updated);
            GlossaryService.getList({ filter: filter, sort: sort, dir: dir, page: page, pageSize: pageSize }, updated)
                .then((response) => {
                    setGlossaryEntries(response);
                    // setTotal(response.data.total);
                })
                .catch((e) => {
                    console.log(e);
                });
        }
    };
    useEffect(updateData, [page, pageSize, dir, sort, filter, settings]);

    useEffect(() => {
        if (auth.user.id) {
            if (auth.user?.roles.indexOf("admin") > -1) {
                setCanContribute(true);
            }
            else {
                setCanContribute(false);
            }
        } else { setCanContribute(false); }
    }, [auth]);

    const handleAdd = (e) => {
        var currentArray = [];
        currentArray.push({
            id: 0,
            name: "",
            description: "",
            content: ""
        });
        glossaryEntries.map((g) => (currentArray.push(g)));        
       
        setGlossaryEntries(currentArray);

    };
    const resetData = () => {
        updateData([page, pageSize, dir, sort, filter]);
    }
    const handleDelete = (entry) => {
        let tempEntries = [...glossaryEntries], ind = -1;
        for (var i = 0; i < tempEntries.length; i++) {
            if (tempEntries[i].id == entry.id) {
                ind = i;
                break;
            }
        }
        if (ind != -1) {
            tempEntries.splice(ind, 1);
            setGlossaryEntries(tempEntries);
        }
    };

    return (
        <React.Fragment>
            <Helmet>
                <title>{AppTitle("Glossary")}</title>
            </Helmet>
            <h2><Box display="flex" justifyContent="space-between"><span>Glossary</span>  {canContribute ? <Tooltip title={"Add new entry"}><IconButton disableRipple={true} size="small" color="primary" onClick={handleAdd}><AddIcon /></IconButton></Tooltip> : <span />}</Box></h2>
          
            {glossaryEntries.map((g) => (
                <div key={g.id}>
                    <EditForm entry={g} canContribute={canContribute} resetData={resetData} onDelete={handleDelete} />
                    <hr />
                </div>
            ))
            }
           
        </React.Fragment>
    );
}

export default Glossary;